import { FormEvent, useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { BrandLockup } from "@/components/Brand";
import { ApiError, api } from "@/api/client";
import type { CurrentUser, InvitePublic } from "@/types";
import { roleLabel } from "@/types";

export function AcceptInvite() {
  const { token = "" } = useParams();
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [invite, setInvite] = useState<InvitePublic | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [fullName, setFullName] = useState("");

  useEffect(() => {
    let cancelled = false;
    api<InvitePublic>(`/api/invites/${token}`)
      .then((inv) => {
        if (cancelled) return;
        setInvite(inv);
        setFullName(inv.full_name ?? "");
      })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof ApiError) {
          if (e.status === 410) setErr("This invite has expired or already been used.");
          else if (e.status === 404) setErr("This invite link is not valid.");
          else setErr(e.message);
        } else {
          setErr("Could not load this invite.");
        }
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (user) {
    return <Navigate to="/home" replace />;
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (password !== password2) {
      setErr("Passwords don't match.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api<CurrentUser>(`/api/invites/${token}/accept`, {
        method: "POST",
        json: { password, full_name: fullName.trim() || null },
      });
      await refresh();
      nav("/home", { replace: true });
    } catch (e) {
      if (e instanceof ApiError) setErr(e.message);
      else setErr("Could not accept invite.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-ink-950">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center mb-8">
          <BrandLockup />
        </div>

        <div className="card">
          <div className="card-header">
            <span className="card-title">Join your agency</span>
          </div>

          {loading ? (
            <div className="p-6 text-sm text-slate-500">Loading invite…</div>
          ) : invite ? (
            <form onSubmit={submit} className="p-6 space-y-4">
              <div className="text-sm text-slate-300">
                You've been invited to join{" "}
                <span className="font-semibold text-slate-100">{invite.agency_name}</span> as a{" "}
                <span className="font-semibold text-accent-400">{roleLabel(invite.role)}</span>
                {invite.inviter_name && (
                  <>
                    {" "}
                    by <span className="text-slate-200">{invite.inviter_name}</span>
                  </>
                )}
                .
              </div>

              <div>
                <label className="label">Email</label>
                <input className="input" value={invite.email} disabled />
              </div>

              <div>
                <label className="label">Full name</label>
                <input
                  className="input"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Giorgi Datashvili"
                />
              </div>

              <div>
                <label className="label">Password</label>
                <input
                  className="input"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                />
              </div>
              <div>
                <label className="label">Confirm password</label>
                <input
                  className="input"
                  type="password"
                  autoComplete="new-password"
                  required
                  minLength={8}
                  value={password2}
                  onChange={(e) => setPassword2(e.target.value)}
                />
              </div>

              {err && (
                <div className="text-[12px] text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-lg px-3 py-2">
                  {err}
                </div>
              )}

              <button
                type="submit"
                disabled={busy || password.length < 8 || password !== password2}
                className="btn-primary w-full"
              >
                {busy ? "Joining…" : "Join agency"}
              </button>
            </form>
          ) : (
            <div className="p-6 text-sm text-rose-300">
              {err || "Could not load this invite."}
            </div>
          )}
        </div>
        <div className="mt-6 text-center text-[11px] text-slate-600">
          AgencyGrid · agencygrid.ge
        </div>
      </div>
    </div>
  );
}
